import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GoalCard from '../components/GoalCard';
import Heatmap from '../components/Heatmap';
import { api } from '../api/client';

export default function Dashboard() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync URL userId to localStorage so API calls work
  useEffect(() => {
    if (userId) {
      localStorage.setItem('userId', userId);
    }
  }, [userId]);

  const fetchDashboard = useCallback(async () => {
    setError('');
    try {
      const uid = localStorage.getItem('userId') || userId;
      if (!uid) {
        setError('No user ID found');
        setLoading(false);
        return;
      }
      //const res = await api.getDashboard();
      
      const json = await api.getDashboard();
      setData(json);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const goals = data?.goals || [];
  const stats = data?.stats || {};
  const heatmap = data?.heatmap || {};

  return (
    <div className="px-4 pt-6 pb-4">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Your Goals</h1>
        <p className="text-sm text-slate-400">
          {stats.totalGoals} goals · {stats.totalLogs} total logs
        </p>
      </div>

      {/* New Goal Button */}
      <button
        onClick={() => navigate('goal')}
        className="w-full bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3.5 px-4 rounded-xl text-sm transition-colors shadow-lg shadow-primary-600/20 active:scale-[0.98] transform mb-6"
      >
        + New Goal
      </button>

      {/* Goals */}
      {goals.length === 0 ? (
        <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700/50">
          <div className="text-4xl mb-3">🎯</div>
          <h3 className="font-semibold mb-2">No goals yet</h3>
          <p className="text-sm text-slate-400 mb-4">Create your first goal to start tracking.</p>
          <button
            onClick={() => navigate('goal')}
            className="bg-primary-600 hover:bg-primary-500 text-white font-medium py-2.5 px-6 rounded-xl text-sm transition-colors"
          >
            Create Goal
          </button>
        </div>
      ) : (
        <div>
          {goals.map((goal: any) => (
            <GoalCard key={goal.id} goal={goal} onUpdate={fetchDashboard} />
          ))}
        </div>
      )}

      {/* Heatmap */}
      {goals.length > 0 && (
        <Heatmap heatmapData={heatmap} goals={goals.map((g: any) => ({ id: g.id, target_value: g.target_value, unit: g.unit, title: g.title, goal_type: g.goal_type }))} />
      )}
    </div>
  );
}
